import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Gift, Trash2, CheckCircle, XCircle, Tag } from 'lucide-react';
import { formatVoucherAmount, getVoucherStatusColor, calculateVouchersStats } from '../../utils/voucherHelpers';

const VouchersManagementModal = ({
  show,
  onClose,
  user,
  vouchers,
  stats,
  selectedVouchers,
  onToggleSelection,
  onDeleteVoucher,
  onBulkDelete,
  onCleanup,
  loading
}) => {
  if (!show || !user) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-4">
                <Tag className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Voucher Management</h2>
                <p className="text-gray-600">{user.firstName} {user.lastName}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <XCircle className="w-6 h-6" />
            </button>
          </div>

          {/* Voucher Stats */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-sm text-blue-600 font-medium">Total</p>
                <p className="text-2xl font-bold text-blue-800">{stats.total}</p>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <p className="text-sm text-green-600 font-medium">Active</p>
                <p className="text-2xl font-bold text-green-800">{stats.active}</p>
              </div>
              <div className="bg-purple-50 rounded-lg p-4">
                <p className="text-sm text-purple-600 font-medium">Used</p>
                <p className="text-2xl font-bold text-purple-800">{stats.used}</p>
              </div>
              <div className="bg-red-50 rounded-lg p-4">
                <p className="text-sm text-red-600 font-medium">Expired</p>
                <p className="text-2xl font-bold text-red-800">{stats.expired}</p>
              </div>
              <div className="bg-orange-50 rounded-lg p-4">
                <p className="text-sm text-orange-600 font-medium">Saved</p>
                <p className="text-2xl font-bold text-orange-800">${stats.totalSaved.toFixed(2)}</p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-between items-center mb-4">
            <div className="flex gap-2">
              {selectedVouchers.length > 0 && (
                <button
                  onClick={onBulkDelete}
                  className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Selected ({selectedVouchers.length})
                </button>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => onCleanup('expired')}
                className="flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Cleanup Expired
              </button>
              <button
                onClick={() => onCleanup('all-unused')}
                className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete All Unused
              </button>
            </div>
          </div>

          {/* Vouchers List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
            </div>
          ) : vouchers.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <Tag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Vouchers Found</h3>
              <p className="text-gray-600">This user hasn't created any vouchers yet.</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {vouchers.map((voucher) => {
                const isExpired = new Date(voucher.validUntil) <= new Date();
                const isUsed = voucher.usageCount > 0;
                const isActive = voucher.isActive && !isExpired;

                return (
                  <div
                    key={voucher._id}
                    className={`flex items-center justify-between p-4 border rounded-lg ${
                      isActive ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-300'
                    }`}
                  >
                    {/* Checkbox */}
                    {!isUsed && (
                      <input
                        type="checkbox"
                        checked={selectedVouchers.includes(voucher._id)}
                        onChange={() => onToggleSelection(voucher._id)}
                        className="mr-3 h-5 w-5 text-green-600 border-gray-300 rounded focus:ring-green-500"
                      />
                    )}

                    {/* Voucher Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <code className="text-lg font-mono font-bold text-green-600 bg-green-50 px-3 py-1 rounded">
                          {voucher.code}
                        </code>
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          isActive ? 'bg-green-100 text-green-800' :
                          isExpired ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {isActive ? 'Active' : isExpired ? 'Expired' : 'Inactive'}
                        </span>
                        {isUsed && (
                          <span className="px-2 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">
                            Used ({voucher.usageCount}/{voucher.usageLimit || '∞'})
                          </span>
                        )}
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm text-gray-600">
                        <div>
                          <span className="font-medium">Type:</span>
                          <span className="ml-2">{voucher.type === 'PERCENTAGE' ? `${voucher.value}% Off` : `$${voucher.value} Off`}</span>
                        </div>
                        <div>
                          <span className="font-medium">Min Order:</span>
                          <span className="ml-2">${voucher.minimumOrderAmount || 0}</span>
                        </div>
                        <div>
                          <span className="font-medium">Created:</span>
                          <span className="ml-2">{new Date(voucher.createdAt).toLocaleDateString()}</span>
                        </div>
                        <div>
                          <span className="font-medium">Expires:</span>
                          <span className="ml-2">{new Date(voucher.validUntil).toLocaleDateString()}</span>
                        </div>
                      </div>

                      {voucher.description && (
                        <p className="text-sm text-gray-600 mt-2">{voucher.description}</p>
                      )}
                    </div>

                    {/* Actions */}
                    {!isUsed && (
                      <button
                        onClick={() => onDeleteVoucher(voucher._id)}
                        className="ml-3 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete voucher"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VouchersManagementModal;
